import { ArrowDown, ArrowUp, Check, Cog, Eye, FileQuestion, Hand, Pencil, RotateCcw, SkipForward, Trash2 } from 'lucide-react';
import { type PlanStep, type StepState, type StepType } from '../hooks/useNetworkPlans';
import { formatOperationName } from '../utils/formatting';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';
import ManualStepDetail from './ManualStepDetail';

interface PlanStepListProps {
  steps: PlanStep[];
  /** When provided, manual steps render their checklist + evidence detail. */
  planId?: string;
  editable?: boolean;
  executing?: boolean;
  deviceNames?: Record<string, string>;
  onEdit?: (step: PlanStep) => void;
  onDelete?: (stepId: string) => void;
  onMoveUp?: (stepId: string) => void;
  onMoveDown?: (stepId: string) => void;
  onComplete?: (stepId: string) => void;
  onSkip?: (stepId: string) => void;
  onRetry?: (stepId: string) => void;
}

function getStepOperationName(step: PlanStep): string | null {
  const payload = step.payload as { operations?: { operationName: string }[] } | undefined;
  if (payload?.operations?.[0]?.operationName) {
    return payload.operations[0].operationName;
  }
  return null;
}

/** The first operation's variable values, so reviewers see the actual change
 * (e.g. `vlanId: 20`, `interface: ge-0/0/12`) — not just the operation name. */
function getStepOperationVariables(step: PlanStep): [string, string][] {
  const payload = step.payload as { operations?: { variables?: Record<string, unknown> }[] } | undefined;
  const vars = payload?.operations?.[0]?.variables;
  if (!vars) return [];
  return Object.entries(vars).map(([k, v]) => [k, Array.isArray(v) ? v.join(', ') : String(v)]);
}

const STEP_TYPE_ICON: Record<StepType, typeof Cog> = {
  automated_config: Cog,
  automated_verification: Check,
  manual_task: Hand,
  manual_verification: Eye,
};

const STEP_TYPE_LABEL: Record<StepType, string> = {
  automated_config: 'Config',
  automated_verification: 'Auto verify',
  manual_task: 'Manual task',
  manual_verification: 'Manual verify',
};

const STATE_STYLE: Record<StepState, string> = {
  pending: 'bg-muted text-muted-foreground',
  in_progress: 'bg-sev-5/15 text-sev-5',
  completed: 'bg-sev-4/15 text-sev-4',
  failed: 'bg-sev-1/15 text-sev-1',
  skipped: 'bg-sev-3/15 text-foreground',
};

const STATE_LABEL: Record<StepState, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
};

const isManualStep = (stepType: StepType) =>
  stepType === 'manual_task' || stepType === 'manual_verification';

const VERIFY_LABEL: Record<string, string> = {
  device_reachable: 'Verify: device reachable',
  interface_oper_up: 'Verify: interface up',
  lldp_neighbor: 'Verify: LLDP neighbor',
  mac_seen_on_port: 'Verify: MAC on port',
};

function getVerifyLabel(step: PlanStep): string | null {
  const t = step.autoVerify?.type;
  return typeof t === 'string' ? (VERIFY_LABEL[t] ?? `Verify: ${t}`) : null;
}

/** Soak duration (minutes) if this is a soak/wait step, else null. */
function soakMinutes(step: PlanStep): number | null {
  const soak = (step.payload as { soak?: { durationMinutes?: number } }).soak;
  return typeof soak?.durationMinutes === 'number' ? soak.durationMinutes : null;
}

const ON_FAILURE_LABEL: Record<string, string> = {
  continue: 'on fail: continue',
  rollback: 'on fail: roll back',
};

/** Failure reason recorded by the executor in the step result, if any. */
function getStepError(step: PlanStep): string | null {
  const err = step.result?.error;
  if (err == null) return null;
  return typeof err === 'string' ? err : JSON.stringify(err);
}

/** True while a config step is parked awaiting telemetry verification. */
function isVerifyPending(step: PlanStep): boolean {
  return step.state === 'in_progress' && step.result?.verifyPending === true;
}

/** An in-progress manual step that has run past its declared duration. */
function isStepOverdue(step: PlanStep): boolean {
  if (step.state !== 'in_progress' || step.expectedDurationMinutes == null || !step.startedAt) {
    return false;
  }
  const dueMs = new Date(step.startedAt).getTime() + step.expectedDurationMinutes * 60_000;
  return Date.now() > dueMs;
}

export default function PlanStepList({
  steps,
  planId,
  editable = false,
  executing = false,
  deviceNames = {},
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  onComplete,
  onSkip,
  onRetry,
}: PlanStepListProps) {
  if (steps.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        icon={FileQuestion}
        title="No steps yet"
        description="Add steps to define the actions in this plan."
        size="inline"
      />
    );
  }

  const showActions = editable || executing;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-border-subtle">
          <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium w-10">#</th>
            <th className="px-3 py-2 font-medium w-32">Type</th>
            <th className="px-3 py-2 font-medium">Title</th>
            <th className="px-3 py-2 font-medium w-32">State</th>
            {showActions ? <th className="px-3 py-2 font-medium w-56">Actions</th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {steps.map((step, index) => {
            const isInProgress = step.state === 'in_progress';
            const isFailed = step.state === 'failed';
            const isPending = step.state === 'pending';
            const isCompleted = step.state === 'completed';
            const TypeIcon = STEP_TYPE_ICON[step.stepType];
            const opName = getStepOperationName(step);
            const opVars = getStepOperationVariables(step);

            return (
              <tr
                key={step.id}
                className={cn(
                  'hover:bg-accent/30',
                  isInProgress && executing ? 'bg-sev-5/5' : null,
                )}
              >
                <td className="px-3 py-2 text-muted-foreground">{step.position + 1}</td>
                <td className="px-3 py-2">
                  <Badge variant="secondary" className="font-normal gap-1">
                    <TypeIcon className="w-3 h-3" />
                    {STEP_TYPE_LABEL[step.stepType]}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{step.title}</div>
                  {step.description ? (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {step.description.substring(0, 100)}
                      {step.description.length > 100 ? '…' : ''}
                    </p>
                  ) : null}
                  {!isManualStep(step.stepType) && (step.deviceId || opName) ? (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {step.deviceId && deviceNames[step.deviceId] ? (
                        <Badge variant="outline" className="text-[10px] font-normal">
                          {deviceNames[step.deviceId]}
                        </Badge>
                      ) : null}
                      {opName ? (
                        <Badge className="bg-sev-5/15 text-sev-5 border-0 text-[10px] font-normal">
                          {formatOperationName(opName)}
                        </Badge>
                      ) : null}
                      {opVars.map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-[10px] font-normal font-mono">
                          {k}: {v}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                  {getVerifyLabel(step) ||
                  step.expectedDurationMinutes != null ||
                  soakMinutes(step) != null ||
                  ON_FAILURE_LABEL[step.onFailure] ? (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {soakMinutes(step) != null ? (
                        <Badge className="bg-sev-3/15 text-foreground border-0 text-[10px] font-normal">
                          Soak {soakMinutes(step)}m
                        </Badge>
                      ) : getVerifyLabel(step) ? (
                        <Badge variant="outline" className="text-[10px] font-normal gap-1">
                          <Check className="w-2.5 h-2.5" />
                          {getVerifyLabel(step)}
                        </Badge>
                      ) : null}
                      {step.expectedDurationMinutes != null ? (
                        <Badge variant="outline" className="text-[10px] font-normal">
                          ~{step.expectedDurationMinutes}m
                        </Badge>
                      ) : null}
                      {ON_FAILURE_LABEL[step.onFailure] ? (
                        <Badge variant="outline" className="text-[10px] font-normal text-sev-2">
                          {ON_FAILURE_LABEL[step.onFailure]}
                        </Badge>
                      ) : null}
                    </div>
                  ) : null}
                  {planId && isManualStep(step.stepType) ? (
                    <ManualStepDetail
                      planId={planId}
                      step={step}
                      interactive={executing}
                      showEvidence={!editable}
                    />
                  ) : null}
                  {executing && isCompleted && step.configChangeLogId &&
                    !isManualStep(step.stepType) && step.deviceId ? (
                    <p className="text-xs mt-1">
                      <a
                        href={`/devices/${step.deviceId}?tab=config`}
                        className="text-sev-5 hover:underline"
                      >
                        View config change
                      </a>
                    </p>
                  ) : null}
                  {isFailed && getStepError(step) ? (
                    <p className="text-xs text-sev-1 mt-1.5 whitespace-pre-wrap break-words">
                      {getStepError(step)}
                    </p>
                  ) : null}
                </td>
                <td className="px-3 py-2 align-top">
                  <Badge className={cn('border-0 font-normal', STATE_STYLE[step.state])}>
                    {STATE_LABEL[step.state]}
                  </Badge>
                  {isInProgress && executing ? (
                    <div className="text-xs text-sev-5 mt-1">
                      {isManualStep(step.stepType)
                        ? 'Awaiting action'
                        : isVerifyPending(step)
                          ? 'Verifying…'
                          : 'Running…'}
                    </div>
                  ) : null}
                  {isStepOverdue(step) ? (
                    <Badge className="border-0 font-normal bg-sev-1/15 text-sev-1 mt-1">
                      Overdue
                    </Badge>
                  ) : null}
                </td>
                {showActions ? (
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1.5">
                      {editable ? (
                        <>
                          {onMoveUp && index > 0 ? (
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => { onMoveUp(step.id); }}
                              title="Move up"
                              aria-label="Move up"
                            >
                              <ArrowUp className="w-3.5 h-3.5" />
                            </Button>
                          ) : null}
                          {onMoveDown && index < steps.length - 1 ? (
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => { onMoveDown(step.id); }}
                              title="Move down"
                              aria-label="Move down"
                            >
                              <ArrowDown className="w-3.5 h-3.5" />
                            </Button>
                          ) : null}
                          {onEdit ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 gap-1"
                              onClick={() => { onEdit(step); }}
                              title="Edit"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                              Edit
                            </Button>
                          ) : null}
                          {onDelete ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 gap-1 text-sev-1 hover:text-sev-1"
                              onClick={() => { onDelete(step.id); }}
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Del
                            </Button>
                          ) : null}
                        </>
                      ) : null}
                      {executing ? (
                        <>
                          {isInProgress && isManualStep(step.stepType) && onComplete ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 gap-1 text-sev-4 hover:text-sev-4"
                              onClick={() => { onComplete(step.id); }}
                              title="Mark complete"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Complete
                            </Button>
                          ) : null}
                          {(isPending || isInProgress || isFailed) && onSkip ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 gap-1"
                              onClick={() => { onSkip(step.id); }}
                              title="Skip this step"
                            >
                              <SkipForward className="w-3.5 h-3.5" />
                              Skip
                            </Button>
                          ) : null}
                          {isFailed && onRetry ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 gap-1 text-sev-5 hover:text-sev-5"
                              onClick={() => { onRetry(step.id); }}
                              title="Retry this step"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Retry
                            </Button>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
